import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import {
  AssetService,
  ModalRef,
  ModalService,
  ReactiveComponent,
  MachineInfo,
  TwoButtonModalV2Component,
  TwoButtonModalV2,
  ModalModule,
  ChipV2Module,
  ButtonV2Module,
  FileUploadV2Module,
} from '@schenck/rotec-components';
import { BehaviorSubject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { MachineTaskInfo } from 'src/api';
import { UpsertMachineTaskWizardComponent } from 'src/pages/machine-tasks/upsert-machine-task-wizard/upsert-machine-task-wizard.component';
import { MachineTaskService } from 'src/services';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { TaskListComponent } from '../task-list/task-list.component';

@Component({
  selector: 'ma-machine-details-modal',
  templateUrl: './machine-details-modal.component.html',
  styleUrls: ['./machine-details-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ModalModule,
    TranslatePipe,
    TranslateDirective,
    ChipV2Module,
    TaskListComponent,
    ButtonV2Module,
    AsyncPipe,
    FileUploadV2Module,
  ],
})
export class MachineDetailsModalComponent extends ReactiveComponent {
  private readonly assetService = inject(AssetService);
  private readonly modalService = inject(ModalService);
  private readonly taskService = inject(MachineTaskService);
  private readonly cdr = inject(ChangeDetectorRef);
  public modalRef = inject<ModalRef<MachineInfo>>(ModalRef);

  private machine$ = this.assetService.machine$(this.modalRef.data.id);
  private loadTaskList$ = new BehaviorSubject<null>(null);

  public state = this.connect(
    {
      machine: this.machine$,
      machineGroups: this.assetService.parentGroupsOfMachine$(this.machine$),
      plant: this.assetService.plantOfMachine$(this.machine$),
      tasks: this.loadTaskList$.pipe(
        switchMap(() => this.taskService.tasksByMachine$(this.modalRef.data.id))
      ),
    },
    this.cdr
  );

  constructor() {
    super();
  }

  public openTask(id: string): void {
    this.taskService
      .get$(id)
      .pipe(
        switchMap(
          (machine: MachineTaskInfo) =>
            this.modalService.open(UpsertMachineTaskWizardComponent, machine).events$
        ),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => this.loadTaskList$.next(null));
  }

  public deleteTask(id: string): void {
    this.taskService
      .get$(id)
      .pipe(
        switchMap((task: MachineTaskInfo) =>
          this.modalService
            .open(TwoButtonModalV2Component, <TwoButtonModalV2>{
              title: '',
              text: task.scheduling
                ? `Do you really want to delete this task series: VALUE ?`
                : `Do you really want to delete this task: VALUE ?`,
              textParams: { value: task.title },
              cancelText: 'Cancel',
              confirmText: 'Delete',
              summary: 'Delete Tasks',
              darkMode: true,
              warning: true,
            })
            .events$.pipe(filter((shouldDelete) => !!shouldDelete))
        ),
        switchMap(() => this.taskService.delete$(id)),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => this.loadTaskList$.next(null));
  }
}
